import { ShopStore, FeatureFlagStore } from 'stores';
import { webServiceProvider } from 'shared';

/**
 * Utility functions for testing and debugging feature flags
 */

/**
 * Get a comprehensive view of shop settings including feature flags
 */
export const getShopSettingsDebugInfo = async (
  shopId = ShopStore.currentShop?.id
) => {
  if (!shopId) {
    throw new Error('No shop ID provided');
  }

  try {
    const shopSettings = await ShopStore.getShopSettings(shopId);
    const settings = shopSettings?.result?.[0]?.settings || {};

    return {
      shopId,
      shopName: ShopStore.currentShop?.name,
      allSettings: settings,
      featureFlags: settings.featureFlags || {},
      otherSettings: Object.keys(settings).filter(
        (key) => key !== 'featureFlags'
      ),
      currentFlags: FeatureFlagStore.getAllFlags(),
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Failed to get shop settings debug info:', error);
    throw error;
  }
};

/**
 * Test feature flag operations to ensure they don't override other settings
 */
export const testFeatureFlagOperations = async (
  shopId = ShopStore.currentShop?.id
) => {
  console.log('🧪 Starting feature flag operations test...');

  try {
    // 1. Get initial state
    const beforeTest = await getShopSettingsDebugInfo(shopId);
    console.log('📋 Before test:', beforeTest);

    // 2. Save a feature flag
    console.log('🔧 Testing feature flag save...');
    await FeatureFlagStore.setShopFlag('apiCaching', true, shopId);

    const afterSave = await getShopSettingsDebugInfo(shopId);
    console.log('💾 After save:', afterSave);

    // 3. Verify other settings weren't affected
    const otherSettingsPreserved = beforeTest.otherSettings.every((key) => {
      const before = JSON.stringify(beforeTest.allSettings[key]);
      const after = JSON.stringify(afterSave.allSettings[key]);
      return before === after;
    });

    console.log('✅ Other settings preserved:', otherSettingsPreserved);

    // 4. Test reset operation
    console.log('🔄 Testing feature flag reset...');
    await FeatureFlagStore.resetShopFlags(shopId);

    const afterReset = await getShopSettingsDebugInfo(shopId);
    console.log('🔄 After reset:', afterReset);

    // 5. Verify other settings still preserved after reset
    const otherSettingsStillPreserved = beforeTest.otherSettings.every(
      (key) => {
        const before = JSON.stringify(beforeTest.allSettings[key]);
        const after = JSON.stringify(afterReset.allSettings[key]);
        return before === after;
      }
    );

    console.log(
      '✅ Other settings preserved after reset:',
      otherSettingsStillPreserved
    );

    // 6. Summary
    const testResults = {
      success: otherSettingsPreserved && otherSettingsStillPreserved,
      beforeTest,
      afterSave,
      afterReset,
      otherSettingsPreserved,
      otherSettingsStillPreserved,
    };

    console.log('🎯 Test Results:', testResults);
    return testResults;
  } catch (error) {
    console.error('❌ Test failed:', error);
    throw error;
  }
};

/**
 * Check if shop has any existing settings that could be affected
 */
export const checkExistingShopSettings = async (
  shopId = ShopStore.currentShop?.id
) => {
  try {
    const debugInfo = await getShopSettingsDebugInfo(shopId);

    const analysis = {
      hasExistingSettings: debugInfo.otherSettings.length > 0,
      existingSettingsKeys: debugInfo.otherSettings,
      featureFlagsExist: Object.keys(debugInfo.featureFlags).length > 0,
      existingFeatureFlags: debugInfo.featureFlags,
      recommendation: '',
    };

    if (analysis.hasExistingSettings) {
      analysis.recommendation =
        'Shop has existing settings. Feature flag operations will preserve them.';
    } else {
      analysis.recommendation =
        'Shop has no existing settings. Safe to add feature flags.';
    }

    console.log('🔍 Shop Settings Analysis:', analysis);
    return analysis;
  } catch (error) {
    console.error('Failed to analyze shop settings:', error);
    throw error;
  }
};

/**
 * Developer helper to quickly enable/disable features for testing
 */
export const devToggleFeature = async (
  featureName,
  enabled = true,
  shopId = ShopStore.currentShop?.id
) => {
  if (import.meta.env.MODE !== 'development') {
    console.warn('devToggleFeature is only available in development mode');
    return;
  }

  try {
    console.log(
      `🛠️ DEV: ${enabled ? 'Enabling' : 'Disabling'} ${featureName} for shop ${shopId}`
    );
    await FeatureFlagStore.setShopFlag(featureName, enabled, shopId);
    console.log(
      `✅ DEV: ${featureName} is now ${enabled ? 'enabled' : 'disabled'}`
    );
    console.log('🔄 Note: Flags automatically reload when you switch shops!');
  } catch (error) {
    console.error(`❌ DEV: Failed to toggle ${featureName}:`, error);
  }
};

/**
 * Test shop switching to verify automatic flag reloading
 */
export const testShopSwitching = async () => {
  if (import.meta.env.MODE !== 'development') {
    console.warn('testShopSwitching is only available in development mode');
    return;
  }

  console.log('🧪 Testing automatic flag reloading on shop switch...');

  try {
    const originalShopId = ShopStore.currentShop?.id;
    console.log('🏢 Current shop:', originalShopId);

    // Get available shops
    const shops = Array.from(ShopStore.data.keys()).filter(
      (id) => id !== originalShopId
    );
    if (shops.length === 0) {
      console.log('⚠️ No other shops available for testing');
      return;
    }

    const testShopId = shops[0];
    console.log(`🔄 Switching to shop ${testShopId}...`);

    // Switch shop (this should trigger automatic flag reload)
    ShopStore.setCurrentShop(testShopId);

    // Wait a moment for the reaction to fire
    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log(
      '✅ Shop switch complete. Check logs above for automatic flag reload!'
    );
    console.log(
      '📊 Current flags after switch:',
      FeatureFlagStore.getAllFlags().effective
    );

    // Switch back
    if (originalShopId) {
      console.log(`🔙 Switching back to original shop ${originalShopId}...`);
      ShopStore.setCurrentShop(originalShopId);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('✅ Switched back to original shop!');
    }
  } catch (error) {
    console.error('❌ Shop switching test failed:', error);
  }
};

/**
 * Test the new Cache API implementation
 */
export const testCacheAPI = async () => {
  if (import.meta.env.MODE !== 'development') {
    console.warn('testCacheAPI is only available in development mode');
    return;
  }

  console.log('💾 Testing Cache API implementation...');

  try {
    // Check if Cache API is supported
    if (!('caches' in window)) {
      console.warn('⚠️ Cache API not supported in this browser');
      return;
    }

    // Enable caching for test
    const originalFlag = FeatureFlagStore.isApiCachingEnabled;
    if (!originalFlag) {
      await FeatureFlagStore.setShopFlag('apiCaching', true);
      console.log('🔄 Enabled API caching for test');
    }

    // Get cache stats before
    const statsBefore = await webServiceProvider.getCacheStats();
    console.log('📊 Cache stats before test:', statsBefore);

    // Make a test API call
    console.log('🚀 Making test API call...');
    const testEndpoint = 'shop';
    const testParams = { shopType: 'all' };
    const result1 = await webServiceProvider.getMany(testEndpoint, testParams);
    console.log('✅ First call completed');

    // Make the same call again (should be cached)
    console.log('🚀 Making same API call again (should hit cache)...');
    const result2 = await webServiceProvider.getMany(testEndpoint, testParams);
    console.log('✅ Second call completed (should have been cached)');

    // Get cache stats after
    const statsAfter = await webServiceProvider.getCacheStats();
    console.log('📊 Cache stats after test:', statsAfter);

    // Test cache invalidation
    console.log('🧹 Testing cache invalidation...');
    await webServiceProvider.invalidateCachePattern('shop');

    const statsAfterInvalidation = await webServiceProvider.getCacheStats();
    console.log('📊 Cache stats after invalidation:', statsAfterInvalidation);

    // Restore original flag if needed
    if (!originalFlag) {
      await FeatureFlagStore.setShopFlag('apiCaching', false);
      console.log('🔄 Restored original caching flag');
    }

    console.log('✅ Cache API test completed successfully!');
  } catch (error) {
    console.error('❌ Cache API test failed:', error);
  }
};

// Export for global access in development
if (import.meta.env.MODE === 'development') {
  window.featureFlagUtils = {
    getShopSettingsDebugInfo,
    testFeatureFlagOperations,
    checkExistingShopSettings,
    devToggleFeature,
    testShopSwitching,
    testCacheAPI,
  };

  // Log helpful info for developers
  console.log('🛠️ Dev utilities available:');
  console.log(
    '  - window.featureFlagUtils.devToggleFeature("apiCaching", true)'
  );
  console.log('  - window.featureFlagUtils.testShopSwitching()');
  console.log('  - window.featureFlagUtils.testCacheAPI()');
  console.log('  - window.featureFlagUtils.checkExistingShopSettings()');
}
